import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { ButtonBase } from '@sorare/core/src/atoms/buttons/ButtonBase';
import { FullWidthContainer } from '@sorare/core/src/atoms/layout/FullWidthContainer';
import {
  Horizontal,
  SBHorizontal,
  Vertical,
} from '@sorare/core/src/atoms/layout/flex';
import {
  BodyM,
  BodyS,
  HeadlineM,
  LabelS,
} from '@sorare/core/src/atoms/typography';
import { TeamAvatar } from '@sorare/core/src/components/club/TeamAvatar';
import { useIsTabletAndAbove } from '@sorare/core/src/hooks/device/useIsTabletAndAbove';
import { sortBy } from '@sorare/core/src/lib/arrays';
import { isType } from '@sorare/core/src/lib/gql';
import { getPositionInitials } from '@sorare/core/src/lib/positions';
import { tabletAndAbove } from '@sorare/core/src/style/mediaQuery';

import { ScheduledPlayerGameScoreState } from '@sorare/marketplace/src/components/game/ScheduledPlayerGameScoreState';
import { Grade } from '@sorare/marketplace/src/components/player/Grade';

import { PlayerAvatar } from 'components/player/PlayerAvatar';

import type { MatchPlayingStatusOdds_game } from './__generated__/index.graphql';

const Grid = styled(Vertical)`
  gap: var(--triple-unit);
  @media ${tabletAndAbove} {
    flex-direction: row;
    gap: 0;

    & > *:first-child {
      border-right: 1px solid var(--c-nd-150-opaque);
    }
  }
`;

const Column = styled(FullWidthContainer)`
  display: flex;
  flex-direction: column;
  flex: 1;
  gap: var(--double-unit);
  padding-top: var(--double-unit);
  padding-bottom: var(--double-unit);
  @media ${tabletAndAbove} {
    max-height: calc(100vh - var(--current-stack-height, 78px));
    overflow: auto;
    padding-top: var(--triple-unit);
  }
`;

const StyledButtonBase = styled(ButtonBase)`
  padding: var(--half-unit) var(--container-padding);
  &:hover {
    background: var(--c-nd-50);
  }
`;

const StyledPlayerAvatar = styled(PlayerAvatar)`
  border: 1px solid var(--c-nd-150);
  background-color: var(--c-nd-50);
`;

const TeamHeader = ({
  anyTeam,
  provider,
}: {
  anyTeam:
    | MatchPlayingStatusOdds_game['awayTeam']
    | MatchPlayingStatusOdds_game['homeTeam'];
  provider?: {
    providerRedirectUrl: string;
    providerIconUrl: string;
  };
}) => {
  return (
    <SBHorizontal>
      <Horizontal>
        <TeamAvatar team={anyTeam} size={24} />
        <HeadlineM as="p">
          <FormattedMessage
            id="MatchPlayingStatusOdds.startingProjections"
            defaultMessage="Starting Projections"
          />
        </HeadlineM>
      </Horizontal>
      {!!provider && (
        <a
          href={provider?.providerRedirectUrl}
          target="_blank"
          rel="noopener noreferrer"
        >
          <Horizontal>
            <LabelS as="p" color="var(--c-nd-600)">
              <FormattedMessage
                id="MatchPlayingStatusOdds.provider"
                defaultMessage="Powered by"
              />
            </LabelS>

            <img
              src={provider?.providerIconUrl}
              alt="Provider"
              width="16"
              height="16"
            />
          </Horizontal>
        </a>
      )}
    </SBHorizontal>
  );
};

const TeamPlayingStatusOdds = ({
  playerGameScores,
  anyTeam,
  onPlayerDetailsClick,
}: {
  playerGameScores: MatchPlayingStatusOdds_game['playerGameScores'];
  anyTeam:
    | MatchPlayingStatusOdds_game['homeTeam']
    | MatchPlayingStatusOdds_game['awayTeam'];
  onPlayerDetailsClick: (playerSlug: string) => void;
}) => {
  const isTabletAndAbove = useIsTabletAndAbove();

  return (
    <Vertical grow>
      {(isTabletAndAbove
        ? playerGameScores
        : playerGameScores.slice(0, 13)
      ).map(pgs => {
        const { anyPlayer } = pgs;
        return (
          <SBHorizontal
            key={pgs.id}
            as={StyledButtonBase}
            onClick={() => {
              onPlayerDetailsClick(anyPlayer.slug);
            }}
          >
            <Horizontal>
              <StyledPlayerAvatar player={anyPlayer} variant="medallion" />
              <Vertical gap={0}>
                <BodyM as="p">{anyPlayer?.displayName}</BodyM>
                <Horizontal gap={0.5}>
                  <TeamAvatar team={anyTeam} size={16} />
                  <BodyS as="p" color="var(--c-nd-600)">
                    {anyPlayer.cardPositions
                      .map(position => getPositionInitials(position))
                      .join(', ')}
                  </BodyS>
                </Horizontal>
              </Vertical>
            </Horizontal>
            <Horizontal>
              <ScheduledPlayerGameScoreState
                playerGameScores={[pgs]}
                variant="chip"
              />
              <Grade projection={pgs.projection} />
            </Horizontal>
          </SBHorizontal>
        );
      })}
    </Vertical>
  );
};

type Props = {
  game: MatchPlayingStatusOdds_game;
  onPlayerDetailsClick: (playerSlug: string) => void;
};

export const MatchPlayingStatusOdds = ({
  game,
  onPlayerDetailsClick,
}: Props) => {
  const footballPlayingStatusOdds = isType(
    game.playerGameScores[0]?.anyPlayerGameStats,
    'PlayerGameStats'
  )
    ? game.playerGameScores[0]?.anyPlayerGameStats?.footballPlayingStatusOdds
    : null;

  const pgsSortedByGrade = sortBy(
    item => item.projection?.grade ?? 'Z',
    [...game.playerGameScores]
  );

  const sortedPgsByStarterOdds = sortBy(
    pgs => {
      return isType(pgs.anyPlayerGameStats, 'PlayerGameStats') &&
        pgs.anyPlayerGameStats?.footballPlayingStatusOdds
          ?.starterOddsBasisPoints
        ? -1 *
            pgs.anyPlayerGameStats?.footballPlayingStatusOdds
              ?.starterOddsBasisPoints
        : -1;
    },
    [...pgsSortedByGrade]
  );

  const homeTeamSortedPgsByStarterOdds = sortedPgsByStarterOdds.filter(
    pgs => pgs.anyPlayerGameStats?.anyTeam.slug === game.homeTeam?.slug
  );
  const awayTeamSortedPgsByStarterOdds = sortedPgsByStarterOdds.filter(
    pgs => pgs.anyPlayerGameStats?.anyTeam.slug === game.awayTeam?.slug
  );

  return (
    <Grid>
      <Column>
        <TeamHeader
          anyTeam={game.homeTeam}
          provider={footballPlayingStatusOdds || undefined}
        />
        <FullWidthContainer.NoPadding>
          <TeamPlayingStatusOdds
            playerGameScores={homeTeamSortedPgsByStarterOdds}
            onPlayerDetailsClick={onPlayerDetailsClick}
            anyTeam={game.homeTeam}
          />
        </FullWidthContainer.NoPadding>
      </Column>
      <Column>
        <TeamHeader
          anyTeam={game.awayTeam}
          provider={footballPlayingStatusOdds || undefined}
        />
        <FullWidthContainer.NoPadding>
          <TeamPlayingStatusOdds
            playerGameScores={awayTeamSortedPgsByStarterOdds}
            onPlayerDetailsClick={onPlayerDetailsClick}
            anyTeam={game.awayTeam}
          />
        </FullWidthContainer.NoPadding>
      </Column>
    </Grid>
  );
};

MatchPlayingStatusOdds.fragments = {
  game: gql`
    fragment MatchPlayingStatusOdds_game on Game {
      id
      playerGameScores(includingLegends: false) {
        id
        anyPlayer {
          slug
          displayName
          cardPositions
          ...GameplayPlayerAvatar_anyPlayer
        }
        projection {
          id
          ...Grade_playerGameScoreProjection
        }
        anyPlayerGameStats {
          id
          ... on PlayerGameStats {
            id
            footballPlayingStatusOdds {
              providerIconUrl
              providerRedirectUrl
              starterOddsBasisPoints
            }
          }
          anyTeam {
            slug
          }
        }
        ...ScheduledPlayerGameScoreState_playerGameScore
      }
      awayTeam {
        slug
        ...TeamAvatar_team
      }
      homeTeam {
        slug
        ...TeamAvatar_team
      }
    }
    ${PlayerAvatar.fragments.anyPlayer}
    ${TeamAvatar.fragments.team}
    ${Grade.fragments.projection}
    ${ScheduledPlayerGameScoreState.fragments.playerGameScore}
  ` as TypedDocumentNode<MatchPlayingStatusOdds_game>,
};
